/**
 * CapabilityCardsForm: edit form for the `capability-cards` dynamic block.
 *
 * data: { sectionHeading?, hiddenCards? }
 *
 * Two controls:
 *   1. Section heading text input.
 *   2. Per-card visibility checkboxes — all 7 catalog cards listed; cards
 *      whose ecosystem feature flag is disabled are shown greyed out and
 *      forced unchecked (the flag is the ceiling; the editor toggle is an
 *      opt-out within what the flags allow).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Form } from 'react-bootstrap';

import useIsFlagEnabled from '@/shared/hooks/useIsFlagEnabled';

import CARD_CATALOG from '../../blocks/capabilityCardsConfig';

export default function CapabilityCardsForm({ data, onChange }) {
    const d = data || {};
    const hiddenCards = Array.isArray(d.hiddenCards) ? d.hiddenCards : [];

    const isFlagEnabled = useIsFlagEnabled();

    const handleHeading = (value) => {
        onChange({ ...d, sectionHeading: value });
    };

    const toggleCard = (cardId, checked) => {
        const next = checked
            ? hiddenCards.filter((id) => id !== cardId)
            : [...hiddenCards, cardId];
        onChange({ ...d, hiddenCards: next });
    };

    return (
        <>
            <Form.Group controlId="capability-cards-heading" className="mb-4">
                <Form.Label>Section heading</Form.Label>
                <Form.Control
                    type="text"
                    value={d.sectionHeading || ''}
                    onChange={(e) => handleHeading(e.target.value)}
                    placeholder="What You Can Do Here"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Visible cards</Form.Label>
                <div className="d-flex flex-column gap-2">
                    {CARD_CATALOG.map((card) => {
                        const flagEnabled = isFlagEnabled(card.featureKey);
                        const isChecked =
                            flagEnabled && !hiddenCards.includes(card.id);

                        return (
                            <Form.Check
                                key={card.id}
                                type="checkbox"
                                id={`card-toggle-${card.id}`}
                                checked={isChecked}
                                disabled={!flagEnabled}
                                onChange={(e) =>
                                    toggleCard(card.id, e.target.checked)
                                }
                                label={
                                    <span
                                        className={
                                            !flagEnabled ? 'text-muted' : ''
                                        }
                                    >
                                        {card.icon} {card.heading}
                                        {!flagEnabled && (
                                            <Badge
                                                bg="secondary"
                                                className="ms-2 fw-normal"
                                            >
                                                Feature disabled
                                            </Badge>
                                        )}
                                    </span>
                                }
                            />
                        );
                    })}
                </div>
                <Form.Text muted className="mt-2 d-block">
                    Cards whose feature is disabled by ecosystem settings cannot
                    be shown regardless of this toggle.
                </Form.Text>
            </Form.Group>

            <Alert variant="info" className="mb-0">
                Card visibility updates automatically when ecosystem feature
                settings change. Use the toggles above to hide cards that are
                not relevant to this ecosystem even if the feature is enabled.
            </Alert>
        </>
    );
}

CapabilityCardsForm.propTypes = {
    data: PropTypes.shape({
        sectionHeading: PropTypes.string,
        hiddenCards: PropTypes.arrayOf(PropTypes.string),
    }),
    onChange: PropTypes.func.isRequired,
};

CapabilityCardsForm.defaultProps = {
    data: {},
};
